import type { AiAnnotationDraft, AiProposalEngine } from "@/ai/engines/engine.types";
import { inferProposalTimeSec, proposalHasKeyword } from "@/ai/engines/engineUtils";

const TIMING_KEYWORDS = ["timing", "pace", "pacing", "rhythm", "beat", "tighten", "landing"];

export const timingEngine: AiProposalEngine = (context) => {
  const drafts: AiAnnotationDraft[] = [];

  for (const proposal of context.proposals) {
    const baseTimeSec = inferProposalTimeSec(context, proposal);

    for (const diff of proposal.diffs ?? []) {
      if (diff.kind === "marker/move") {
        const frameOffset = Math.round((diff.toTimeSec - diff.fromTimeSec) * context.fps);
        drafts.push({
          type: "timing",
          subtype: "rhythm_alignment_suggestion",
          severity: Math.abs(frameOffset) >= context.fps ? "warning" : "suggestion",
          summary: `Shift ${diff.markerId} to better align the beat landing.`,
          details: {
            audioMotif: diff.markerId,
            frameOffset,
            syncTarget: diff.markerId,
          },
          proposedAction: "adjust",
          nodeId: `${proposal.id}:${diff.markerId}`,
          timeSec: diff.toTimeSec,
          frame: Math.max(0, Math.floor(diff.toTimeSec * context.fps)),
          action: `Move ${diff.markerId} to ${diff.toTimeSec.toFixed(2)}s`,
        });
      }

      if (diff.kind === "range/add" || diff.kind === "range/update") {
        const startSec = diff.kind === "range/add" ? diff.range.startSec : diff.toStartSec;
        const endSec = diff.kind === "range/add" ? diff.range.endSec : diff.toEndSec;
        const midpointSec = (startSec + endSec) / 2;
        const durationSec = Math.max(0, endSec - startSec);
        drafts.push({
          type: "timing",
          subtype: diff.kind === "range/add" && String(diff.range.kind ?? "").toLowerCase().includes("emotion")
            ? "emotional_landing_adjustment"
            : "beat_density_warning",
          severity: durationSec > 2 ? "suggestion" : "warning",
          summary: `Proposal range ${diff.kind === "range/add" ? diff.range.kind : diff.rangeId} suggests timing refinement.`,
          details: diff.kind === "range/add" && String(diff.range.kind ?? "").toLowerCase().includes("emotion")
            ? {
                emotion: String(diff.range.kind ?? "emotion").trim() || null,
                currentDuration: durationSec,
                recommendedDuration: Math.max(durationSec, 2.5),
                rationale: proposal.rationale,
              }
            : {
                densityScore: durationSec > 0 ? 1 / durationSec : null,
                draggingOrRushed: durationSec < 1 ? "rushed" : durationSec > 4 ? "dragging" : "balanced",
                suggestedTrimOrHold: durationSec < 1 ? "hold_longer" : durationSec > 4 ? "trim_range" : "keep_window",
              },
          proposedAction: "adjust",
          nodeId: `${proposal.id}:${diff.kind === "range/add" ? diff.range.id : diff.rangeId}`,
          timeSec: midpointSec,
          frame: Math.max(0, Math.floor(midpointSec * context.fps)),
          action: "Adjust timing around proposal range",
        });
      }
    }

    if (!proposal.diffs?.length && proposalHasKeyword(proposal, TIMING_KEYWORDS)) {
      drafts.push({
        type: "timing",
        subtype: "emotional_landing_adjustment",
        severity: "suggestion",
        summary: proposal.rationale.trim() || "Timing refinement suggested.",
        details: {
          emotion: null,
          currentDuration: null,
          recommendedDuration: null,
          rationale: proposal.rationale,
        },
        proposedAction: "adjust",
        nodeId: proposal.id,
        timeSec: baseTimeSec,
        frame: Math.floor(baseTimeSec * context.fps),
        action: "Review pacing at the current beat",
      });
    }
  }

  return drafts;
};